import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import client, { authClient } from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { getRoles } from 'utils/helpers';
import { isOwner, isOwnerPortal, ROLE } from 'utils/isOwner';
import { getDeviceId } from '../../../utils/helpers';
import { oldGloryBaseUrl } from 'config/apiConfig';

export interface AuthData {
    access_token: string;
    app_name: string | null;
    expires_in: number | null;
    jti: string;
    last_signon_timestamp: number | null;
    refresh_token: string;
    scope: string;
    tenant_name: string;
    token_type: string;
    user_type: string;
    uuid: string;
    need_change_password?: boolean;
}
interface AuthState {
    data: AuthData | null;
    roles?: (keyof typeof ROLE)[];
    loading?: boolean;
    error?: boolean;
    isMerchant: boolean;
}

const initialState: AuthState = {
    data: null,
    roles: [],
    loading: false,
    error: false,
    isMerchant: false,
};

type UserData = {
    [key: string]: number | undefined;
};

interface LoginPayload {
    username: string;
    password: string;
}

const totalAttempts = 10;
const updateUserAttempts = (userId: string) => {
    const userDataJSON = localStorage.getItem('userAttempts');
    let userData: UserData = {};

    if (userDataJSON) {
        userData = JSON.parse(userDataJSON) || {};
    }
    if (userData?.[userId]) {
        userData[userId] = (userData?.[userId] || 0) + 1;
    } else {
        userData[userId] = 1;
    }
    const attempts = totalAttempts - (userData?.[userId] || 1);
    if (attempts > 0) {
        notify(`You have ${totalAttempts - (userData?.[userId] || 1)} attempts left! After that your account will be locked.`, 'error');
        localStorage.setItem('userAttempts', JSON.stringify(userData));
    }
};

const removeUserAttempts = (userId: string) => {
    const userDataJSON = localStorage.getItem('userAttempts');
    let userData: UserData = {};

    if (userDataJSON) {
        userData = JSON.parse(userDataJSON);
    }
    if (!userData?.[userId]) return;

    delete userData[userId];
    localStorage.setItem('userAttempts', JSON.stringify(userData));
};

export const login = createAsyncThunk('auth/login', async ({ username, password }: LoginPayload, { dispatch}) => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');
    const response: any = await authClient.post('/auth/oauth/token', params.toString());
    const { access_token, refresh_token, need_change_password } = response || {};
    if (!access_token) {
        // handling this because backend do not return the number of failed attempts and we want to show the user how many /failed attempts has made so he do not lock his account accidentally
        return updateUserAttempts(username);
    }
    removeUserAttempts(username);
    const roles = getRoles(access_token);

    if (!isOwnerPortal(roles)) {
        notify('Your are a lower role to continue.', 'error');
        return;
    }
    authClient.setToken(access_token, refresh_token);
    client.setToken(access_token, refresh_token);

    if (!need_change_password) {
        const deviceId = await getDeviceId();
        const validate2FAResponse:any = await dispatch(validate2FA({ username, deviceId }));
        if (validate2FAResponse?.payload?.user_two_factor_auth_required) {
            // calling getUserInfo here to show the email of the user in 2faType modal
            return { response, is2FARequired: true };
        }
    }

    localStorage.setItem('token', JSON.stringify(response));
    return { response, roles, access_token, refresh_token, username };
});

export const forgotPassword = createAsyncThunk('auth/forgotPassword', async (username: string) => {
    const res: any = await authClient.post('/auth/api/reset/password', { username }, { 'Content-Type': 'application/json' });
    return res;
});
export const changePassword = createAsyncThunk(
    'auth/changePassword',
    async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
        const res: any = await authClient.post(
            '/auth/api/myinfo/changePassword',
            { currentPassword, newPassword },
            { 'Content-Type': 'application/json' }
        );
        return res;
    }
);
export const validate2FA = createAsyncThunk(
    'validate2FactorAuthentication',
    async ({ username, deviceId, pin2fa }: { username: string; deviceId?: string; pin2fa?: string }) => {
        const response = await client.post(`${oldGloryBaseUrl}/validate2FA`, { username, deviceId, pin2fa });
        return response;
    }
);

export const request2FAPin = createAsyncThunk(
    'request2FactorAuthentication',
    async ({ twoFactorAuthType, username }: { twoFactorAuthType: string; username: string }) => {
        const response: any = await client.post(`${oldGloryBaseUrl}/request2FAPin`, { twoFactorAuthType, username });
        return response;
    }
);

export const registerDevice = createAsyncThunk(
    'registerDevice',
    async (payload: { username: string; pin2fa: string; deviceId: string }) => {
        const response: any = await client.post(`${oldGloryBaseUrl}/registerDevice`, payload);
        return response;
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        updateAuthInfo(state, action: PayloadAction<AuthData>) {
            const { payload } = action || {};
            if (!payload) return;
            state.data = payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(login.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(login.fulfilled, (state, action) => {
            state.loading = false;
            const { payload } = action;
            if (!payload) return;
            const { response, roles, is2FARequired } = payload || {};
            // we do not update redux in case 2fa is required so it will not update the redux
            // and will create the issue of login if user click the browser back button
            if (!response || is2FARequired) return;
            state.data = response;
            if (roles?.length) {
                state.roles = roles || [];
                state.isMerchant = isOwner(roles);
            }
        });
        builder.addCase(login.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export const { updateAuthInfo } = authSlice.actions;
export default authSlice.reducer;
